import { Injectable, Injector, NgZone, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import {
  BehaviorSubject, Observable, Subject,
  filter, finalize, map, take, tap, throwError,
} from 'rxjs';
import { jwtDecode } from 'jwt-decode';

import { URLs } from '../../../../environments/environment';
import { STORAGE } from '../../constants/constants.core';
import { ApiResponse } from '../../interfaces/api-response.interface';
import { AuthData, AuthUser, LoginRequest } from '../../interfaces/auth.interface';
import { LocalStorageService } from '../localStorage/local-storage.service';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http            = inject(HttpClient);
  private readonly localStorageSvc = inject(LocalStorageService);
  private readonly injector        = inject(Injector);
  private readonly ngZone          = inject(NgZone);

  // Lazy para evitar dependencia circular: AuthService → Router → authGuard → AuthService
  private get router(): Router { return this.injector.get(Router); }

  private readonly _user       = signal<AuthUser | null>(null);
  readonly user       = this._user.asReadonly();

  private readonly _tipoUsuario   = signal<string | null>(null);
  readonly tipoUsuario = this._tipoUsuario.asReadonly();

  private readonly _rolesActivos  = signal<string[]>([]);
  readonly rolesActivos = this._rolesActivos.asReadonly();

  private readonly _loggingOut = signal(false);
  readonly loggingOut = this._loggingOut.asReadonly();

  // ─── Estado de sesión ────────────────────────────────────────────────────
  readonly sessionActive$  = new BehaviorSubject<boolean>(false);
  readonly sessionWarning$ = new BehaviorSubject<boolean>(false);

  // ─── Coordinación de refresh token ───────────────────────────────────────
  refreshInProgress = false;
  readonly refreshInProgress$ = new BehaviorSubject<boolean>(false);
  readonly refreshTokenSubject = new BehaviorSubject<string | null>(null);
  readonly refreshDone$ = new Subject<string>();

  // Avisa 60 segundos antes de que expire el token
  readonly warningAdvanceMs = 60_000;

  private warningTimeoutId?: ReturnType<typeof setTimeout>;
  private logoutTimeoutId?: ReturnType<typeof setTimeout>;
  private warningTriggeredByChannel = false;
  private logoutTriggeredByChannel  = false;
  private _isLoggingOut             = false;
  private _lastActivityMs           = Date.now();

  // Umbral de actividad: si el usuario interactuó en los últimos 10 min se considera activo
  private readonly activityThresholdMs = 10 * 60_000;

  private readonly channel = new BroadcastChannel('auth_channel');

  constructor() {
    // Inicializar usuario desde storage al arrancar
    const rawUser = this.localStorageSvc.getItem(STORAGE.USER);
    if (rawUser) {
      this._user.set(JSON.parse(rawUser) as AuthUser);
    }
    // Fuente 1: JWT | Fuente 2: localStorage | Fuente 3: dominio email
    // Usamos || (no ??) porque getItem devuelve '' cuando no existe la clave
    const rolFromToken = this._extractRolFromToken();
    const storedType   = this.localStorageSvc.getItem(STORAGE.USER_TYPE);
    const emailRol     = this._rolFromEmail(this._user()?.email);

    const rol = rolFromToken || storedType || emailRol || null;
    if (rol) {
      this._tipoUsuario.set(rol);
      this.localStorageSvc.addItem(STORAGE.USER_TYPE, rol);
    }

    // Registrar actividad del usuario para el refresh silencioso
    (['mousemove', 'keydown', 'click', 'scroll'] as const).forEach(ev =>
      document.addEventListener(ev, () => { this._lastActivityMs = Date.now(); }, { passive: true })
    );

    // Sincronización entre pestañas
    this.channel.onmessage = ({ data }) => {
      const { type, token } = data;
      this.ngZone.run(() => {
        if (type === 'refresh-start') {
          this.refreshInProgress = true;
          this.refreshInProgress$.next(true);
          this.refreshTokenSubject.next(null);
        }
        if (type === 'refresh-done') {
          this.refreshInProgress = false;
          this.sessionWarning$.next(false);
          this.refreshInProgress$.next(false);
          if (token) {
            this.localStorageSvc.addItem(STORAGE.TOKEN, token);
            this.refreshDone$.next(token);
            this.refreshTokenSubject.next(token);
          }
        }
        if (type === 'warning') {
          this.warningTriggeredByChannel = true;
          this.sessionWarning$.next(true);
        }
        if (type === 'logout') {
          this.logoutTriggeredByChannel = true;
          this._forceClear();
        }
        if (type === 'warning-clear') {
          this.sessionWarning$.next(false);
        }
      });
    };

    // Si hay sesión activa al iniciar, programar los timers
    if (this.getToken()) {
      this.sessionActive$.next(true);
      this._scheduleTimers();
    }
  }

  // ─── Operaciones de autenticación ────────────────────────────────────────

  login(credentials: LoginRequest): Observable<ApiResponse<AuthData>> {
    return this.http.post<ApiResponse<AuthData>>(URLs.login, credentials);
  }

  handleLoginSuccess(data: AuthData): void {
    this.localStorageSvc.addItem(STORAGE.TOKEN, data.token);
    this.localStorageSvc.addItem(STORAGE.REFRESH_TOKEN, data.refreshToken);
    this.localStorageSvc.addItem(STORAGE.USER, JSON.stringify(data.user));
    this._user.set(data.user);
    // Fuente 1: tipoUsuario del response | Fuente 2: JWT | Fuente 3: dominio email
    const rol = data.tipoUsuario
      || this._extractRolFromToken()
      || this._rolFromEmail(data.user?.email);
    if (rol) {
      const rolUp = rol.toUpperCase();
      this.localStorageSvc.addItem(STORAGE.USER_TYPE, rolUp);
      this._tipoUsuario.set(rolUp);
    }
    this.sessionActive$.next(true);
    this.channel.postMessage({ type: 'warning-clear' });
    this._scheduleTimers();
  }

  /** Almacena solo el access token temporalmente. Usado en el callback de OAuth. */
  setTemporaryToken(token: string): void {
    this.localStorageSvc.addItem(STORAGE.TOKEN, token);
  }

  getUserInfo(): Observable<ApiResponse<AuthUser>> {
    return this.http.post<ApiResponse<AuthUser>>(`${URLs.auth}/info`, {});
  }

  isAdmin(): boolean {
    return this._tipoUsuario()?.toUpperCase() === 'AD';
  }

  isDocente(): boolean {
    return this._tipoUsuario()?.toUpperCase() === 'DC';
  }

  private _rolFromEmail(email?: string | null): string | null {
    if (!email) return null;
    if (email.endsWith('@uqroo.mx'))      return 'AL';
    if (email.endsWith('@uqroo.edu.mx'))  return 'AD';
    return null;
  }

  private _extractRolFromToken(): string | null {
    const token = this.getToken();
    if (!token) return null;
    try {
      const decoded = jwtDecode<Record<string, unknown>>(token);
      console.debug('[AuthService] JWT claims:', decoded);

      const clean = (v: string) => v.replace(/^ROLE_/i, '').toUpperCase();

      // Buscar en campos escalares (prueba todos los nombres posibles)
      const scalar = (decoded['USER_ROL']
        ?? decoded['userRol']
        ?? decoded['rol']
        ?? decoded['tipoUsuario']
        ?? decoded['userType']
        ?? decoded['role']) as string | undefined;
      if (scalar) return clean(String(scalar));

      // Spring Security suele usar "authorities" como array de objetos [{authority:"AD"}]
      // o como array de strings ["AD"]
      const authorities = decoded['authorities'] ?? decoded['roles'] ?? decoded['ROLES'];
      if (Array.isArray(authorities) && authorities.length > 0) {
        const first = authorities[0];
        if (typeof first === 'string') return clean(first);
        if (typeof first === 'object' && first !== null) {
          const authority = (first as Record<string, unknown>)['authority']
                         ?? (first as Record<string, unknown>)['rol'];
          if (authority) return clean(String(authority));
        }
      }

      return null;
    } catch {
      return null;
    }
  }

  setTipoUsuario(rol: string): void {
    this._tipoUsuario.set(rol);
    this.localStorageSvc.addItem(STORAGE.USER_TYPE, rol);
  }

  setRolesActivos(roles: string[]): void {
    const normalizados = [...new Set(roles.map(r => r.toUpperCase()))];
    this._rolesActivos.set(normalizados);
  }

  hasRol(rol: string): boolean {
    return this._rolesActivos().includes(rol.toUpperCase());
  }

  logout(): void {
    if (this._isLoggingOut) return;
    this._isLoggingOut = true;
    this._loggingOut.set(true);
    this._forceClear();
    this.channel.postMessage({ type: 'logout' });
  }

  /** Verifica que exista token Y que no haya expirado */
  isAuthenticated(): boolean {
    const token = this.getToken();
    if (!token) return false;
    return this.getRemainingMs() > 0;
  }

  // ─── Acceso a tokens ─────────────────────────────────────────────────────

  getToken(): string | null {
    const value = this.localStorageSvc.getItem(STORAGE.TOKEN);
    return value || null;
  }

  getRefreshToken(): string | null {
    const value = this.localStorageSvc.getItem(STORAGE.REFRESH_TOKEN);
    return value || null;
  }

  // ─── Refresh token ───────────────────────────────────────────────────────

  refreshAccessToken(): Observable<string> {
    if (this.refreshInProgress) {
      return this.refreshTokenSubject.pipe(
        filter((t): t is string => t !== null),
        take(1)
      );
    }

    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      this.logout();
      return throwError(() => new Error('No refresh token available'));
    }

    this.refreshInProgress = true;
    this.refreshInProgress$.next(true);
    this.refreshTokenSubject.next(null);
    this.channel.postMessage({ type: 'refresh-start' });

    return this.http.post<{ token: string }>(URLs.refreshToken, {}, {
      headers: { Authorization: `Bearer ${refreshToken}` },
    }).pipe(
      tap(({ token }) => {
        this.localStorageSvc.addItem(STORAGE.TOKEN, token);
        this.channel.postMessage({ type: 'refresh-done', token });
        this.refreshTokenSubject.next(token);
        this.refreshDone$.next(token);
        this._scheduleTimers();
      }),
      map(({ token }) => token),
      finalize(() => {
        this.refreshInProgress = false;
        this.refreshInProgress$.next(false);
      })
    );
  }

  // ─── JWT utils ───────────────────────────────────────────────────────────

  getRemainingMs(): number {
    const token = this.getToken();
    if (!token) return 0;
    try {
      const { exp } = jwtDecode<{ exp: number }>(token);
      return exp * 1000 - Date.now();
    } catch {
      return 0;
    }
  }

  // ─── Timers de sesión ────────────────────────────────────────────────────

  private _scheduleTimers(): void {
    this._clearTimers();
    const remaining = this.getRemainingMs();
    if (remaining <= 0) { this.logout(); return; }

    this.warningTimeoutId = setTimeout(() => {
      if (this.warningTriggeredByChannel) return;

      const userIsActive = Date.now() - this._lastActivityMs < this.activityThresholdMs;
      if (userIsActive) {
        // El usuario está activo: renovar el token en silencio
        this.refreshAccessToken().subscribe({
          error: () => {
            // Si el refresh falla, mostrar el warning como fallback
            this.sessionWarning$.next(true);
            this.channel.postMessage({ type: 'warning' });
          },
        });
      } else {
        this.sessionWarning$.next(true);
        this.channel.postMessage({ type: 'warning' });
      }
    }, Math.max(0, remaining - this.warningAdvanceMs));

    this.logoutTimeoutId = setTimeout(() => {
      if (!this.logoutTriggeredByChannel) {
        this.logout();
      }
    }, remaining);
  }

  private _clearTimers(): void {
    clearTimeout(this.warningTimeoutId);
    clearTimeout(this.logoutTimeoutId);
    this.warningTriggeredByChannel = false;
    this.logoutTriggeredByChannel  = false;
    this.sessionWarning$.next(false);
  }

  private _forceClear(): void {
    this.localStorageSvc.removeItem(STORAGE.TOKEN);
    this.localStorageSvc.removeItem(STORAGE.REFRESH_TOKEN);
    this.localStorageSvc.removeItem(STORAGE.USER);
    this.localStorageSvc.removeItem(STORAGE.USER_TYPE);
    this._tipoUsuario.set(null);
    this._clearTimers();
    this._user.set(null);
    this._loggingOut.set(false);
    this._isLoggingOut = false;
    this.sessionActive$.next(false);
    this.router.navigate(['/auth/login']);
  }
}
